import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Tooltip,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  Switch,
  Divider,
  IconButton,
  CircularProgress,
  Collapse,
  SvgIcon
} from "@material-ui/core";
import CodeIcon from '@material-ui/icons/Code';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import ClearIcon from '@material-ui/icons/Clear';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { fields } from "components/explore/resultsGrid/gridHelpers/fields";
import { connect } from "react-redux";
import { compose } from "recompose";
import PalettePicker from "components/explore/gmap/PalettePicker";
import {
  setExploreRequestedGroupLevel,
  setExplorePinDisplayType,
  setExploreRequestedPinDisplayType,
  setPinColorIds,
  setPinColorPaletteId,
  setExploreFilter,
  setExploreGeoFilter,
  setExploreActiveDrawingObject,
  setExploreActiveEditingObject,
  updateExploreObjectById,
  setExplorePinsAreVisible,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  setIsSideBarOpen
} from 'store/actions';
import { MapRefContext } from 'components/explore/Explore';
import { viewObjects } from 'store/explore/views';
import { prettifyFilterQuery } from 'components/explore/views/helpers';
import classNames from 'classnames';
import { sendClientLog } from "services/logService";
import { Constants } from 'utilities/constants';
import { getManualPropertyGroupLevelForZoomLevel } from "api/rex/spatial";
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';
import FilterBuilderGrid from "components/explore/views/FilterBuilderGrid";
import { getColorForShape, isLatLngOnTheScreen, getIQRBounds } from 'components/explore/gmap/helpers';
import { getPinColorIcon, colorPalettePresets } from "components/explore/gmap/colorHelper";
import { formatValue } from "utilities/format";
import { propertyGroupLevels } from "api/rex/spatial";
import FilterRulesViewer from '../../FilterRulesViewer'

function PropertyDataDetail({
  classes,
  object,
  propertyData,
  propertiesCount,
  filter,
  filterGeo,
  groupLevel,
  requestedGroupLevel,
  pinColorIds,
  pinColorPaletteId,
  mapType,
  zoom,
  autoBreakoutThreshold,
  pinDisplayType,
  requestedPinDisplayType,
  propertyDataDisplay,
  isMainIndicatorVisible,
  setExploreRequestedGroupLevel,
  setExplorePinDisplayType,
  setExploreRequestedPinDisplayType,
  setExploreFilter,
  setExploreGeoFilter,
  setPinColorIds,
  setPinColorPaletteId,
  activeDrawingObject,
  activeEditingObject,
  setExploreActiveDrawingObject,
  setExploreActiveEditingObject,
  otherVisiblePropertyDataObjectIds,
  updateExploreObjectById,
  setExplorePinsAreVisible,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  isMobile,
  setIsSideBarOpen
}) {
  const mapRef = useContext(MapRefContext);
  const [isSwitchToAuto, setIsSwitchToAuto] = useState(false);
  const [viewFilterRulesAsSQL, setViewFilterRulesAsSQL] = useState(false);
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const filterValue = filter?.filterValue;

  const prevRequestedGroupLevel = useRef(null);
  useEffect(() => {
    const isSwitch = prevRequestedGroupLevel.current != null && prevRequestedGroupLevel.current !== 'auto' && requestedGroupLevel === 'auto';
    if (isSwitch) {
      setIsSwitchToAuto(true);
    }
    prevRequestedGroupLevel.current = requestedGroupLevel;
  }, [requestedGroupLevel]);

  const prevIndicatorVisible = useRef(null);
  useEffect(() => {
    const isSwitch = prevIndicatorVisible.current === true && isMainIndicatorVisible === false;
    if (isSwitch && isSwitchToAuto) {
      setIsSwitchToAuto(false);
    }
    prevIndicatorVisible.current = isMainIndicatorVisible;
  }, [isMainIndicatorVisible, isSwitchToAuto]);

  const handleHideOtherPropertyDataObjects = () => {
    otherVisiblePropertyDataObjectIds.forEach(id => {
      updateExploreObjectById(id, { visible: false });
      setExplorePinsAreVisible(id, false);
    })
    sendClientLog('Info', `Explore Views - User clicked Hide All Others on PropertyDataDetail for object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleViewUnfilteredDataClick = () => {
    setExploreFilter(object.id, { filterValue: null, filterExpression: null });
    sendClientLog('Info', `Explore Views - User clicked View Unfiltered Data on PropertyDataDetail for object id ${object.id}, layer id ${object.layerId}`);
  }

  const onPinColorChange = e => {
    setPinColorIds(object.id, { ...pinColorIds, [mapType]: e.target.value });
  }

  const handlePaletteChange = paletteId => {
    setPinColorPaletteId(object.id, paletteId);
  }

  const onPinDisplayTypeChange = (e) => {
    var field = e.target.value !== 'none' ? fields.find(f => f.dataField === e.target.value) : null;
    if (e.target.value === 'none') {
      setExplorePinDisplayType(object.id, null)
    } else {
      setExploreRequestedPinDisplayType(object.id, field);
    }
  };

  function handleViewFilterRulesAsSql() {
    setViewFilterRulesAsSQL(!viewFilterRulesAsSQL);
    sendClientLog('Info', `Explore Views - User toggled viewFilterRulesAsSQL to ${!viewFilterRulesAsSQL} for object id ${object.id}, layer id ${object.layerId}`);
  }

  const handleStartDrawing = () => {
    setExploreActiveDrawingObject({
      id: object.id,
      layerId: object.layerId,
      type: object.type
    });
    if(isMobile) {
      setIsSideBarOpen(false)
    }
  }

  const handleDrawingCancel = () => {
    setExploreActiveDrawingObject(null);
  }

  const handleDrawingCreate = () => {
    setExploreActiveDrawingObject({ ...activeDrawingObject, isCreating: true });
    sendClientLog('Info', `Explore Views - User applied geo filter for Property Data object id ${object.id}, layer id ${object.layerId}`);
  }

  const fitBounds = () => {
    const bounds = new window.google.maps.LatLngBounds();

    if (
      object.type === 'PropertyData'
      && object.filterGeo?.length
      && !isLatLngOnTheScreen(object.filterGeo, mapRef?.current)
    ) {
      object.filterGeo.forEach(latLng => { bounds.extend(latLng) });
      fitBoundsWithZoom(mapRef.current, bounds);
    }
  }

  const handleEdit = () => {
    fitBounds();
    setExploreActiveEditingObject({ id: object.id, type: object.type, drawingMode: viewObjects[object.type].drawingMode });
    if(isMobile) {
      setIsSideBarOpen(false)
    }
  }

  const handleEditingCancel = () => {
    setExploreActiveEditingObject({ id: object.id, type: object.type, drawingMode: viewObjects[object.type].drawingMode, isCancelling: true });
  }

  const handleEditingDone = () => {
    setExploreActiveEditingObject({ id: object.id, type: object.type, drawingMode: viewObjects[object.type].drawingMode, isSaving: true });
    sendClientLog('Info', `Explore Views - User applied edits to geo filter for Property Data object id ${object.id}, layer id ${object.layerId}`);
  }

  const onClearGeoFilterClick = () => {
    if (filterGeo != null) {
      setExploreGeoFilter(object.id, null);
      sendClientLog('Info', `Explore Views - User clicked Clear on geo filter for Property Data object id ${object.id}, layer id ${object.layerId}`);
    }
  }

  const onClearFilterClick = () => {
    setExploreFilter(object.id, { filterValue: null, filterExpression: null });
    sendClientLog('Info', `Explore Views - User clicked Clear on data filter for Property Data object id ${object.id}, layer id ${object.layerId}`);
  }

  const improvedFilter = useMemo(() => {
    return prettifyFilterQuery(filterValue, fields, true);
  }, [filterValue]);

  const handleDataSourceChange = e => {
    updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, selectedSourceId: object.id } });
    if (object.isFilterSet) {
      fetchExplorePropertyData({ objectId: object.id, shouldFetch: true });
    }
    sendClientLog('Info', `Explore Views - User clicked Set Source to This Object on PropertyDataDetail for object id ${object.id}, layer id ${object.layerId}`);
  }

  const pinColors = useMemo(() => {
    const toRemove = [Constants.ViewsPropertyDataPinColor.ORANGE, Constants.ViewsPropertyDataPinColor.YELLOW, Constants.ViewsPropertyDataPinColor.GRAY]; // Remove system colors (orange for selected, gray for null values)
    const colors = Object.keys(Constants.ViewsPropertyDataPinColor)
      .map(key => Constants.ViewsPropertyDataPinColor[key])
      .filter(value => !toRemove.includes(value));
    return colors;
  }, []);

  const colorMatrix = useMemo(() => {
    if (!pinDisplayType) return {};
    const fieldDataType = pinDisplayType.dataType;
    const pinColorPalette = colorPalettePresets.find(palette => palette.id === pinColorPaletteId);
    const shapeWithValue = propertyData.find(prop => prop[pinDisplayType.dataField] != null); // Find a nonnull value to use to guarantee that getColorForShape returns value colors
    const colorMatrix = getColorForShape(pinColorPalette, shapeWithValue, propertyData, pinDisplayType.dataField, fieldDataType); //ok to pass any single shape as placeholder

    const formatColorMatrixValue = (value, allowDecimal) => {
      if (value != null && pinDisplayType && pinDisplayType.format) {
        const format = { ...pinDisplayType.format };
        if (format.decimals === 0 && allowDecimal && !Number.isInteger(value)) {
          format.decimals = 1;
        }
        return formatValue(value, format, false, "-n/a-");
      }
      return "-n/a-";
    };
    
    const targetValueCount = 30;
    const uniqueValueCount = new Set(propertyData.map(pin => formatValue(pin[pinDisplayType.dataField], pinDisplayType.format, true, "-n/a-"))).size;
    const allowDecimal = ['beds', 'fullBaths'].includes(pinDisplayType.dataField);
    colorMatrix.exceedsTargetCount = uniqueValueCount > targetValueCount;
    colorMatrix.minValueDisplay = formatColorMatrixValue(colorMatrix.minValue);
    colorMatrix.midValueDisplay = formatColorMatrixValue(colorMatrix.midValue, allowDecimal);
    colorMatrix.maxValueDisplay = formatColorMatrixValue(colorMatrix.maxValue);
    const filteredData = propertyData.filter(pin => pin[pinDisplayType.dataField] != null).map(el => el[pinDisplayType.dataField]);
    const iqrBounds = getIQRBounds(filteredData);
    const range = colorMatrix.maxValue - colorMatrix.minValue || 1;
    if (iqrBounds.lower != null && iqrBounds.upper != null) {
      colorMatrix.iqrLowerPct = (iqrBounds.lower - colorMatrix.minValue) / range * 100;
      colorMatrix.iqrUpperPct = (iqrBounds.upper - colorMatrix.minValue) / range * 100;
      colorMatrix.iqrMiddlePct = (colorMatrix.iqrUpperPct + colorMatrix.iqrLowerPct) / 2;
      colorMatrix.iqrLowerValue = iqrBounds.lower;
      colorMatrix.iqrUpperValue = iqrBounds.upper;
      colorMatrix.iqrLowerValueDisplay = formatColorMatrixValue(iqrBounds.lower);
      colorMatrix.iqrUpperValueDisplay = formatColorMatrixValue(iqrBounds.upper);
      colorMatrix.iqrMiddleValueDisplay = formatColorMatrixValue((iqrBounds.upper + iqrBounds.lower) / 2, allowDecimal);
    }
    // console.log(colorMatrix)
    return colorMatrix;
  }, [pinColorPaletteId, pinDisplayType, propertyData]);

  function onRequestedGroupLevelChange(event, value) {
    const shouldFetch = object.isFilterSet && value.props.value !== groupLevel; // Skip fetch if requested level is already the auto level
    setExploreRequestedGroupLevel(object.id, value.props.value, shouldFetch);
  }

  const getGroupLevels = useCallback(() => {
    return getManualPropertyGroupLevelForZoomLevel(zoom);
  }, [zoom]);

  const isEditing = activeEditingObject?.id === object.id;
  const isGeoFilterSet = object.filterGeo;
  const isFetchingWithNoFilter = object.isFilterSet && object.filter?.filterValue === null && !isGeoFilterSet;

  const getGroupLevelCaption = useCallback((groupLevel) => {
    return propertyGroupLevels.find(level => level.value === groupLevel)?.caption;
  }, []);

  return (
    <div className={classes.panelWrapper}>
      {otherVisiblePropertyDataObjectIds.length > 0 &&
        <>
          <div className={classes.sectionContainer}>
            <div className={classes.propertyDataObjectCountContainer}>
              <Typography variant='subtitle2' color='inherit' className={classes.filterMessage} >{`${otherVisiblePropertyDataObjectIds.length} other Property Data object${otherVisiblePropertyDataObjectIds.length > 1 ? 's' : ''} on map`}</Typography>
              <Button
                color="primary"
                size='small'
                onClick={handleHideOtherPropertyDataObjects}
                variant='outlined'
                style={{ marginLeft: 16 }}
              >
                {`Hide all others`}
              </Button>
            </div>
          </div>
          <Divider />
        </>
      }
      <div className={classes.sectionContainer}>
        <div style={{ display: 'flex' }}>
          <Typography variant='subtitle2' color='inherit' className={classes.sectionTitle} style={{ marginRight: 8 }}>{'Filter'}</Typography>
          {isMainIndicatorVisible && !requestedPinDisplayType &&
            <CircularProgress size={24} />
          }
        </div>
        {!object.isFilterSet &&
          <div className={classes.filterMessageContainer}>
            <InfoOutlinedIcon style={{ fill: '#ffe049', margin: 8 }} />
            <Typography variant='subtitle2' color='inherit' className={classes.filterMessage} >{'Filter has not been set'}</Typography>
            <div style={{ flexGrow: 1 }}></div>
            <Button
              color="primary"
              size='small'
              onClick={handleViewUnfilteredDataClick}
              variant='outlined'
              style={{ marginRight: 4 }}
            >
              {`View unfiltered data`}
            </Button>
          </div>
        }
        {isFetchingWithNoFilter &&
          <div className={classes.filterMessageContainer}>
            <InfoOutlinedIcon style={{ fill: '#ffe049', margin: 8 }} />
            <Typography
              variant='subtitle2'
              className={classes.filterMessage}
              color='inherit' >
              Viewing with no filter
            </Typography>
          </div>
        }
        <div className={classNames(classes.editorBlock, isGeoFilterSet && classes.editorActiveFilter)}>
          <div className={classes.editorControls}>
            <Typography variant='subtitle2' color='inherit' className={classes.filterTitle}>{'Geo Filter'}</Typography>
            <div style={{ flexGrow: 1 }}></div>
            <Tooltip title={isGeoFilterSet ? 'Edit' : 'Add'}>
              <IconButton
                onClick={isGeoFilterSet ? handleEdit : handleStartDrawing}
                size='small'
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                style={{ margin: '1px 4px' }}
              >
                {isGeoFilterSet ? <EditIcon /> : <AddIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={'Clear'}>
              <span>
                <IconButton
                  onClick={onClearGeoFilterClick}
                  size='small'
                  color='inherit'
                  classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                  style={{ margin: '1px 4px' }}
                  disabled={!isGeoFilterSet}
                >
                  <ClearIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <div className={classNames(classes.editorBlock, filterValue && classes.editorActiveFilter)}>
          <div className={classes.editorControls}>
            <Typography variant='subtitle2' color='inherit' className={classes.filterTitle}>{'Data Filter'}</Typography>
            <div style={{ flexGrow: 1 }}></div>
            {filterValue && (
              <Tooltip title={!viewFilterRulesAsSQL ? 'View SQL' : 'View Comfy'}>
                <span>
                  <IconButton
                    onClick={handleViewFilterRulesAsSql}
                    size='small'
                    color='inherit'
                    classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                    style={{ margin: '0 4px' }}
                  >
                    {!viewFilterRulesAsSQL ? <CodeIcon /> :
                      <SvgIcon>
                        <path d="M12,15H10V13H12V15M18,15H14V13H18V15M8,11H6V9H8V11M18,11H10V9H18V11M20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20M4,6V18H20V6H4Z" />
                      </SvgIcon>
                    }

                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={filterValue ? 'Edit' : 'Add'}>
              <IconButton
                onClick={() => setFilterBuilderOpen(true)}
                size='small'
                color='inherit'
                classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                style={{ margin: '1px 4px' }}
              >
                {filterValue ? <EditIcon /> : <AddIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={'Clear'}>
              <span>
                <IconButton
                  onClick={onClearFilterClick}
                  size='small'
                  color='inherit'
                  classes={{ root: classes.iconButton, disabled: classes.headerIconDisabled }}
                  style={{ margin: '1px 4px' }}
                  disabled={!filterValue}
                >
                  <ClearIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          {filterValue && filterValue.length > 0 && (
            <>
              <Divider /> 
              <div className={classes.filterExpressionContainer}>
                {!viewFilterRulesAsSQL && (
                  <FilterRulesViewer
                    filterValue={filterValue}
                    fields={fields}
                  ></FilterRulesViewer>
                )}
                {viewFilterRulesAsSQL && (
                  <Typography
                    className={classes.filterExpression}
                    color='inherit' >
                    {improvedFilter}
                  </Typography>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <Divider />
      <div className={classes.sectionContainer}>
        <Typography variant='subtitle2' color='inherit' className={classes.sectionTitle}>{'Clustering'}</Typography>
        <Select
          disableUnderline
          value={requestedGroupLevel ?? 'none'}
          onChange={onRequestedGroupLevelChange}
          displayEmpty
          name="groupLevel"
          className={classes.coloringTypeDropDown}
          classes={{
            select: classes.select
          }}
        >
          <MenuItem className={classes.coloringTypeDropDownItem} value="auto" variant="caption">
            {requestedGroupLevel === 'auto' && groupLevel && !isSwitchToAuto ? `Auto - ${getGroupLevelCaption(groupLevel)}` : 'Auto'}
          </MenuItem>
          {
            getGroupLevels().map(type => {
              const exceedsBreakout = type.value === 'Property' && propertiesCount > autoBreakoutThreshold;
              return <MenuItem key={type.value}
                variant="caption"
                className={classes.coloringTypeDropDownItem}
                value={type.value}
                disabled={!type.enabled || exceedsBreakout}>
                {type.caption}
              </MenuItem>
            })
          }
        </Select>
      </div>
      <Divider />
      {object.isFilterSet &&
        <>
          <div className={classes.sectionContainer}>
            <Typography variant='subtitle2' color='inherit' className={classes.sectionTitle}>{'Pin Label'}</Typography>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Select
                disableUnderline
                value={requestedPinDisplayType?.dataField ?? pinDisplayType?.dataField ?? 'none'}
                onChange={onPinDisplayTypeChange}
                displayEmpty
                name="pinDisplayType"
                className={classes.dropDown}
                disabled={requestedPinDisplayType != null}
              >
                <MenuItem
                  key='none'
                  variant="caption"
                  className={classes.dropDownItem}
                  value={'none'}
                >
                  None
                </MenuItem>
                {fields.map(field => {
                  if (field.inPinDisplay) {
                    return <MenuItem key={field.dataField}
                      variant="caption"
                      className={classes.dropDownItem}
                      value={field.dataField}
                    >
                      {field.caption}
                    </MenuItem>
                  }
                })}
              </Select>
              {requestedPinDisplayType &&
                <CircularProgress size={24} />
              }
              <div style={{ flexGrow: 1, minWidth: 16 }} />
              {
                pinDisplayType && pinDisplayType.dataType === 'number' && colorMatrix.minValueColor &&
                <div>
                  <Typography variant='subtitle2' style={{ marginBottom: -4 }}>Coloring range</Typography>
                  <div style={{
                    margin: '4px 0',
                    borderRadius: 3,
                    minWidth: 130,
                    display: 'flex',
                    justifyContent: 'space-between',
                    overflow: 'hidden',
                    position: 'relative',
                    height: 25
                  }}>
                    {/* <div style={{ position: 'absolute', width: '50%', height: '100%', background: `linear-gradient(to right, ${colorMatrix.minValueColor ?? colorMatrix.valueColor}, ${colorMatrix.midValueColor ?? colorMatrix.valueColor})` }}></div> */}
                    {/* <div style={{ position: 'absolute', width: '50%', height: '100%', background: `linear-gradient(to right, ${colorMatrix.midValueColor ?? colorMatrix.valueColor}, ${colorMatrix.maxValueColor ?? colorMatrix.valueColor})`, right: 0 }}></div> */}
                    <div style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(to right,
                        ${colorMatrix.minValueColor ?? colorMatrix.valueColor} 0%,
                        ${colorMatrix.midValueColor ?? colorMatrix.valueColor} 50%,
                        ${colorMatrix.maxValueColor ?? colorMatrix.valueColor} 100%
                      )`,
                      right: 0
                    }}></div>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{`${colorMatrix.exceedsTargetCount && colorMatrix.minValue < colorMatrix.iqrLowerValue ? '<' : ''}${colorMatrix.exceedsTargetCount ? colorMatrix.iqrLowerValueDisplay : colorMatrix.minValueDisplay}`}</Typography>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{colorMatrix.exceedsTargetCount ? colorMatrix.iqrMiddleValueDisplay : colorMatrix.midValueDisplay}</Typography>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{`${colorMatrix.exceedsTargetCount && colorMatrix.maxValue > colorMatrix.iqrUpperValue ? '>' : ''}${colorMatrix.exceedsTargetCount ? colorMatrix.iqrUpperValueDisplay : colorMatrix.maxValueDisplay}`}</Typography>
                  </div>
                  <Typography variant='subtitle2' style={{ marginBottom: -4 }}>Total range</Typography>
                  <div style={{
                    margin: '4px 0',
                    borderRadius: 3,
                    minWidth: 130,
                    display: 'flex',
                    justifyContent: 'space-between',
                    overflow: 'hidden',
                    position: 'relative',
                    height: 25
                  }}>
                    {/* <div style={{ position: 'absolute', width: '50%', height: '100%', background: `linear-gradient(to right, ${colorMatrix.minValueColor ?? colorMatrix.valueColor}, ${colorMatrix.midValueColor ?? colorMatrix.valueColor})` }}></div> */}
                    {/* <div style={{ position: 'absolute', width: '50%', height: '100%', background: `linear-gradient(to right, ${colorMatrix.midValueColor ?? colorMatrix.valueColor}, ${colorMatrix.maxValueColor ?? colorMatrix.valueColor})`, right: 0 }}></div> */}
                    <div style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(to right,
                        ${colorMatrix.minValueColor ?? colorMatrix.valueColor} 0%,
                        ${colorMatrix.minValueColor ?? colorMatrix.valueColor} ${colorMatrix.iqrLowerPct ?? '0'}%,
                        ${colorMatrix.midValueColor ?? colorMatrix.valueColor} ${colorMatrix.iqrMiddlePct ?? '50'}%,
                        ${colorMatrix.maxValueColor ?? colorMatrix.valueColor} ${colorMatrix.iqrUpperPct ?? '100'}%,
                        ${colorMatrix.maxValueColor ?? colorMatrix.valueColor} 100%
                      )`,
                      right: 0
                    }}></div>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{colorMatrix.minValueDisplay}</Typography>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{colorMatrix.midValueDisplay}</Typography>
                    <Typography variant="caption" className={classes.coloringLegendItemText}>{colorMatrix.maxValueDisplay}</Typography>
                  </div>
                </div>
              }
            </div>
          </div>
          <Divider />
        </>
      }
      {pinDisplayType && pinDisplayType.dataType === 'number'
        ? <>
          <div className={classes.sectionContainer}>
            <Typography variant='subtitle2' color='inherit' className={classes.sectionTitle}>{'Pin Palette'}</Typography>
            <PalettePicker palettes={colorPalettePresets} selectedPaletteId={pinColorPaletteId} onPaletteChange={handlePaletteChange} disabled={!pinDisplayType} />
          </div>
          <Divider />
        </>
        : <>
          <div className={classes.sectionContainer}>
            <Typography variant='subtitle2' color='inherit' className={classes.sectionTitle}>{'Pin Color'}</Typography>
            <Select
              disableUnderline
              value={pinColorIds?.[mapType] ?? ''}
              onChange={onPinColorChange}
              displayEmpty
              name="pinColor"
              className={classes.dropDown}
            >
              {pinColors.map(color => {
                return <MenuItem key={color}
                  variant="caption"
                  className={classes.dropDownItem}
                  value={color}
                >
                  <div className={classes.pinColorIconContainer}>
                    <img alt='' src={getPinColorIcon(color)} />
                  </div>
                </MenuItem>
              })}
            </Select>
          </div>
          <Divider />
        </>
      }
      <div style={{ flexGrow: 1 }}></div>
      <div className={classes.controlsContainer}>
        <Collapse in={propertyDataDisplay.visible && propertyDataDisplay.selectedSourceId !== object.id}>
          <div className={classes.filterMessageContainer}>
            <InfoOutlinedIcon style={{ fill: '#ffe049', margin: 8 }} />
            <Typography
              variant='subtitle2'
              className={classes.filterMessage}
              color='inherit' >
              This object is not the current data source for the grid
            </Typography>
            <Button
              color="primary"
              size='small'
              onClick={handleDataSourceChange}
              variant='outlined'
              style={{ margin: '4px 4px 4px 16px' }}
            >
              {`Set source to this object`}
            </Button>
          </div>
        </Collapse>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={<Switch checked={propertyDataDisplay.visible} onChange={() => updateSelectedExploreView({ propertyDataDisplay: { ...propertyDataDisplay, visible: !propertyDataDisplay.visible } })} />}
            label={`${isMobile ? 'Enable' : 'Show'} property data grid/charts`}
            // disabled={isLoading || !selectedView}
          />
        </div>
      </div>
      <FilterBuilderGrid object={object} filterBuilderOpen={filterBuilderOpen} setFilterBuilderOpen={setFilterBuilderOpen} />
      {activeDrawingObject &&
        <div className={classes.drawingOverlay}>
          <Typography>Click on the map to draw the filter</Typography>
          <div>
            <Button variant='outlined' color='primary' className={classes.button} style={{ marginRight: 8 }} onClick={handleDrawingCancel}>Cancel</Button>
            <Button variant='contained' color='primary' className={classes.button} disabled={!activeDrawingObject.isPlaced} onClick={handleDrawingCreate}>Apply</Button>
          </div>
        </div>
      }
      {isEditing &&
        <div className={classes.drawingOverlay}>
          <Typography>Edit the drawn filter and click Apply when finished</Typography>
          <div>
            <Button variant='outlined' color='primary' className={classes.button} style={{ marginRight: 8 }} onClick={handleEditingCancel}>Cancel</Button>
            <Button variant='contained' color='primary' className={classes.button} onClick={handleEditingDone}>Apply</Button>
          </div>
        </div>
      }
    </div>
  );
}

const styles = (theme) => ({
  locationToggleContainer: {
    margin: '-44px 0px 1px 4px',
    display: 'flex',
    alignItems: 'center'
  },
  tooltipIcon: {
    zIndex: 1,
    fontSize: 16,
    marginLeft: 4
  },
  footnote: {
    paddingTop: 10,
  },
  spacer: {
    height: 16,
  },
  savedFilterButtons: {
    fontSize: 12,
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6
  },
  queryBuilder: {
    '& div.e-rule-field': {
      display: 'flex',
      flexDirection: 'column'
    },
    '& div.e-rule-filter': {
      // width: '400px !important'
      width: '100%'
    },
    '& div.e-rule-value-delete': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginLeft: 6
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 461
  },
  sectionContainer: {
    padding: '8px 24px 16px'
  },
  sectionTitle: {
    marginLeft: -8,
    marginBottom: 4,
    fontSize: '1rem'
  },
  filterTitle: {
    display: 'inline'
  },
  filterExpressionContainer: {
    padding: 8,
    minHeight: 36,
    maxHeight: 300,
    overflow: 'auto'
  },
  filterExpression: {
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.8rem',
  },
  panelWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    overflow: 'auto'
  },
  filterEditor: {
    '& div.dx-filterbuilder-group': {
      overflow: 'hidden',
    },
  },
  button: {
    marginTop: 8
  },
  drawingOverlay: {
    position: 'absolute',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    zIndex: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)'
  },
  propertyDataObjectCountContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: -8
  },
  currentFilterContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ededed',
    borderRadius: 4,
    padding: '4px 8px',
    minHeight: 48
  },
  filterMessageContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fffedb',
    border: '1px solid #ffe049',
    borderRadius: 4,
  },
  filterMessage: {
    marginTop: 2
  },
  editorContainer: {
    display: 'flex',
    marginTop: 8
  },
  editor: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '50%',
    borderRadius: 4,
    padding: '4px 8px',
    border: '1px solid #cdcdcd',
    backgroundColor: '#ededed'
  },
  editorBlock: {
    marginTop: 8,
    borderRadius: 4,
    border: '1px solid #cdcdcd',
    width: '100%'
  },
  editorActiveFilter: {
    border: `1px solid ${theme.palette.secondary.main}`,
    backgroundColor: theme.palette.secondary.light
  },
  editorControls: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
  },
  pinColorIconContainer: {
    height: 20,
    width: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  coloringLegendItemText: {
    marginLeft: 5,
    marginRight: 5,
    marginTop: 3,
    marginBottom: 3,
    color: '#ffffff',
    zIndex: 1
  },
  controlsContainer: {
    padding: '8px 24px',
    marginTop: -1,
    borderTop: '1px solid #dddddd',
    zIndex: 1
  },
  dropDown: {
    marginRight: 8,
    height: 'min-content'
  },
  filterRulesViewerContainer: {
    marginLeft: '5px',
    marginBottom: '5px',
    padding: '5px',
    maxHeight: 300,
    overflow: 'auto'
  }
});

function mapState(state, ownProps) {
  const { exploreData, exploreMap, exploreViews } = state;
  const propertyDataObject = exploreData.propertyDataObjects[ownProps.object.id] ?? {};
  const otherVisiblePropertyDataObjectIds = exploreViews.selectedView.objects.filter(obj => {
    const pdo = exploreData.propertyDataObjects[obj.id];
    const pdoHasData = pdo?.propertyData.length;
    return obj.type === Constants.ViewsObjectType.PROPERTYDATA && obj.id !== ownProps.object.id && obj.visible && pdoHasData
  }).map(obj => obj.id);
  return {
    user: state.appState.user,
    propertyData: propertyDataObject.propertyData,
    propertiesCount: propertyDataObject.propertiesCount,
    filter: propertyDataObject.filter,
    filterGeo: propertyDataObject.filterGeo,
    groupLevel: propertyDataObject.groupLevel,
    requestedGroupLevel: propertyDataObject.requestedGroupLevel,
    pinDisplayType: propertyDataObject.pinDisplayType,
    requestedPinDisplayType: propertyDataObject.requestedPinDisplayType,
    pinColorIds: propertyDataObject.pinColorIds,
    pinColorPaletteId: propertyDataObject.pinColorPaletteId,
    mapType: exploreMap.mapType,
    zoom: exploreMap.zoom,
    autoBreakoutThreshold: exploreMap.markerOptions.autoBreakoutThreshold,
    activeDrawingObject: exploreViews.activeDrawingObject,
    activeEditingObject: exploreViews.activeEditingObject,
    otherVisiblePropertyDataObjectIds,
    propertyDataDisplay: exploreViews.selectedView.propertyDataDisplay,
    isMainIndicatorVisible: propertyDataObject.isMainIndicatorVisible,
    isMobile: state.appState.isMobile,
  };
}

const mapDispatch = {
  setExploreRequestedGroupLevel,
  setExplorePinDisplayType,
  setExploreRequestedPinDisplayType,
  setExploreFilter,
  setExploreGeoFilter,
  setPinColorIds,
  setPinColorPaletteId,
  setExploreActiveDrawingObject,
  setExploreActiveEditingObject,
  updateExploreObjectById,
  setExplorePinsAreVisible,
  updateSelectedExploreView,
  fetchExplorePropertyData,
  setIsSideBarOpen
};

export default compose(withStyles(styles), connect(mapState, mapDispatch))(PropertyDataDetail);